import type { TStockOpName } from '@/interfaces/assistant.interface';
import type { IGpthelpTool, IGpthelpToolGate } from '../tool-types';
import { proposalToolOffered } from '../proposal-kinds';
import { BOTH_TOOLS_SENTENCE, createProposalTool } from '../proposal-tool';
import type { TProposalText } from '../proposal-tool';

/** The description's first sentences (plan P5-3): what it prepares and how. */
const DESCRIPTION_START = [
  'Prepares inventory changes for THIS branch that the user reviews and applies with one tap on the card: record a purchase, a loss or a physical count; create or edit stock items (supplies); set the recipe (supplies used per unit) of a product, a variant or an option choice.',
  'Nothing changes until the user applies it; every change can be undone for 7 days (a movement is undone with an opposite movement).',
  'Take ids from the stock and menu read tools; never guess.',
  "Quantities use the stock item's unit.",
];

/** The description's last sentences (plan P5-3). */
const DESCRIPTION_END = [
  'Call it ONCE per request with every change.',
  'The card shows the changes: never list them, and never say something changed before the user applies it.',
  'Plus plan.',
];

/**
 * The description for the member's gate (plan P5-3, F10): the «both tools»
 * sentence only while the menu's write tool is offered too; the static
 * definition has none.
 */
function descriptionFor(gate: IGpthelpToolGate | null): string {
  const both = gate !== null && proposalToolOffered('menu.changes', gate);
  return [
    ...DESCRIPTION_START,
    ...(both ? [BOTH_TOOLS_SENTENCE] : []),
    ...DESCRIPTION_END,
  ].join(' ');
}

/** The fields of each op, in the `op` property's description (plan P5-3). */
const OP_DOCS: Readonly<Record<TStockOpName, TProposalText>> = {
  recordStock: () =>
    "recordStock: movement ('purchase' adds, 'loss' subtracts, 'count' sets the counted total), items [{stockItemId, quantity}], note?.",
  createStockItem: () =>
    "createStockItem: name, unit ('g','kg','ml','l','un','pcs'), cost? (per unit), minimumQuantity?, quantity? (initial), stockType? ('ingredient' default, 'product'), ref? (other changes of this call use it as stockItemRef).",
  updateStockItem: () =>
    'updateStockItem: stockItemId, name?, unit? (quantities of this stock item in the same call are in the new unit), cost?, minimumQuantity?.',
  setRecipe: () =>
    'setRecipe: itemId (+ sizeId for a variant) or optionGroupId + choiceId, lines [{stockItemId or stockItemRef, quantity}] (replaces that recipe; [] removes it).',
};

/**
 * `stock_proposeChanges` (plan P5-3, Y1): prepares an inert inventory
 * proposal of this branch through the Action API; the member applies it from
 * the card. Offered while writes are on for the store, to members with
 * CatalogManage here, with the stock ops not switched off for them (every
 * plan: below Plus each op answers with its plan card).
 */
export const stockProposeTool: IGpthelpTool = createProposalTool({
  kind: 'stock.changes',
  description: descriptionFor,
  opDocs: OP_DOCS,
});
